import { useState, useEffect, useRef } from 'react';
import { ApiEndpoint } from '../types';

interface UseApiEndpointsOptions {
  formDataCalls: Array<{
    method: string;
    name?: string;
    url: string;
    headers: string | Record<string, string>;
    body: string;
  }>;
  callType: 'live' | 'post';
  onSync: (
    calls: Array<{
      method: string;
      name: string;
      url: string;
      headers: Record<string, string>;
      body: string;
      call_type: string;
    }>
  ) => void;
}

const headersDictToArray = (
  headers: string | Record<string, string> | undefined
): { name: string; value: string }[] => {
  if (!headers) return [];
  try {
    const parsed = typeof headers === 'string' ? JSON.parse(headers || '{}') : headers;
    if (typeof parsed === 'object' && parsed !== null && !Array.isArray(parsed)) {
      return Object.entries(parsed).map(([name, value]) => ({ name, value: String(value) }));
    }
    return [];
  } catch {
    return [];
  }
};

export const useApiEndpoints = ({ formDataCalls, callType, onSync }: UseApiEndpointsOptions) => {
  const isInitializedRef = useRef(false);
  const hasInitializedFromFormDataRef = useRef(false);
  const lastFormDataRef = useRef<string>('');
  const onSyncRef = useRef(onSync);

  useEffect(() => {
    onSyncRef.current = onSync;
  }, [onSync]);

  const [apiEndpoints, setApiEndpoints] = useState<ApiEndpoint[]>(() => {
    if (formDataCalls && formDataCalls.length > 0) {
      hasInitializedFromFormDataRef.current = true;
      lastFormDataRef.current = JSON.stringify(formDataCalls);
      return formDataCalls.map((call, index) => ({
        id: `${Date.now()}-${index}`,
        method: call.method || 'POST',
        name: 'name' in call ? (call as { name?: string }).name || '' : '',
        url: call.url || '',
        headers: headersDictToArray(call.headers),
        body: call.body || '',
      }));
    }
    return [
      {
        id: Date.now().toString(),
        method: 'POST',
        name: '',
        url: '',
        headers: [],
        body: '',
      },
    ];
  });

  useEffect(() => {
    isInitializedRef.current = true;
  }, []);

  useEffect(() => {
    const currentFormDataStr = JSON.stringify(formDataCalls);

    if (formDataCalls && formDataCalls.length > 0) {
      if (
        !hasInitializedFromFormDataRef.current ||
        currentFormDataStr !== lastFormDataRef.current
      ) {
        hasInitializedFromFormDataRef.current = true;
        lastFormDataRef.current = currentFormDataStr;
        setApiEndpoints(
          formDataCalls.map((call, index) => ({
            id: `${Date.now()}-${index}`,
            method: call.method || 'POST',
            name: 'name' in call ? (call as { name?: string }).name || '' : '',
            url: call.url || '',
            headers: headersDictToArray(call.headers),
            body: call.body || '',
          }))
        );
      }
    } else if (
      !hasInitializedFromFormDataRef.current &&
      (!formDataCalls || formDataCalls.length === 0)
    ) {
      hasInitializedFromFormDataRef.current = true;
      lastFormDataRef.current = currentFormDataStr;
    }
  }, [formDataCalls]);

  useEffect(() => {
    if (!isInitializedRef.current) return;

    const transformedCalls = apiEndpoints
      .filter(endpoint => endpoint.url && endpoint.url.trim() !== '')
      .map(endpoint => {
        const headersDict: Record<string, string> = {};
        if (endpoint.headers && Array.isArray(endpoint.headers)) {
          endpoint.headers.forEach(header => {
            if (
              header.name &&
              header.value &&
              header.name.trim() !== '' &&
              header.value.trim() !== ''
            ) {
              headersDict[header.name.trim()] = header.value.trim();
            }
          });
        }
        return {
          method: endpoint.method,
          name: endpoint.name || '',
          url: endpoint.url,
          headers: headersDict,
          body: endpoint.body || '',
          call_type: callType,
        };
      });
    onSyncRef.current(transformedCalls);
    lastFormDataRef.current = JSON.stringify(transformedCalls);
  }, [apiEndpoints, callType]);

  const updateApiEndpoint = (
    endpointId: string,
    field: keyof ApiEndpoint,
    value: string | { name: string; value: string }[]
  ) => {
    setApiEndpoints(prev =>
      prev.map(endpoint =>
        endpoint.id === endpointId ? { ...endpoint, [field]: value } : endpoint
      )
    );
  };

  const addApiEndpoint = () => {
    const newEndpoint: ApiEndpoint = {
      id: Date.now().toString(),
      method: 'POST',
      name: '',
      url: '',
      headers: [],
      body: '',
    };
    setApiEndpoints([...apiEndpoints, newEndpoint]);
  };

  const removeApiEndpoint = (id: string) => {
    setApiEndpoints(apiEndpoints.filter(endpoint => endpoint.id !== id));
  };

  const addHeader = (endpointId: string) => {
    updateApiEndpoint(endpointId, 'headers', [
      ...(apiEndpoints.find(e => e.id === endpointId)?.headers || []),
      { name: '', value: '' },
    ]);
  };

  const removeHeader = (endpointId: string, headerIndex: number) => {
    const endpoint = apiEndpoints.find(e => e.id === endpointId);
    if (endpoint) {
      const newHeaders = endpoint.headers.filter((_, index) => index !== headerIndex);
      updateApiEndpoint(endpointId, 'headers', newHeaders);
    }
  };

  const updateHeader = (
    endpointId: string,
    headerIndex: number,
    field: 'name' | 'value',
    value: string
  ) => {
    const endpoint = apiEndpoints.find(e => e.id === endpointId);
    if (endpoint) {
      const newHeaders = [...endpoint.headers];
      newHeaders[headerIndex] = { ...newHeaders[headerIndex], [field]: value };
      updateApiEndpoint(endpointId, 'headers', newHeaders);
    }
  };

  return {
    apiEndpoints,
    updateApiEndpoint,
    addApiEndpoint,
    removeApiEndpoint,
    addHeader,
    removeHeader,
    updateHeader,
  };
};
